import { useState } from "react";
import { gymClient, ApiError } from "../apiClient/gymClient";
import { isBookingCancellable, parseBookingSlotId } from "../utils/dates";
import { GYM_RULES } from "../types/gym.types";
import type { Booking } from "../types/gym.types";

interface CancelBookingButtonProps {
  booking: Booking;
  onCancelled: (booking: Booking) => void;
}

export function CancelBookingButton({ booking, onCancelled }: CancelBookingButtonProps) {
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { date, startTime } = parseBookingSlotId(booking.slotId);
  const cancellable = isBookingCancellable(date, startTime);

  async function handleConfirm() {
    setSubmitting(true);
    setError(null);
    try {
      const cancelled = await gymClient.cancelBooking(booking.id);
      onCancelled(cancelled);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't cancel this booking.");
      setSubmitting(false);
      setConfirming(false);
    }
  }

  if (!cancellable) {
    return (
      <span className="cancel-note">
        Too late to cancel — within {GYM_RULES.cancellationCutoffHours}h of start
      </span>
    );
  }

  if (confirming) {
    return (
      <div className="cancel-confirm">
        <span>Cancel this booking?</span>
        <button type="button" className="btn btn-ghost" onClick={() => setConfirming(false)} disabled={submitting}>
          No
        </button>
        <button type="button" className="btn btn-danger" onClick={handleConfirm} disabled={submitting}>
          {submitting ? "Cancelling…" : "Yes, cancel"}
        </button>
      </div>
    );
  }

  return (
    <div className="cancel-wrap">
      <button type="button" className="btn btn-danger-ghost" onClick={() => setConfirming(true)}>
        Cancel
      </button>
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}
